import { css } from '@emotion/css'
// @ts-expect-error
import ct from 'countries-and-timezones'
import countryToCurrency from 'country-to-currency'
import dayjs from 'dayjs'
import { currency } from 'src/data/currency'
import * as mutations from 'src/graphql/mutations'
import type { ReduxCompany, ReduxQuote } from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import { projectTypes, quoteTypes } from 'src/utils/typeUtils'

function delayInMs(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

function updatedParseFloat(input: any) {
	const parsedInput = Number.parseFloat(input)
	// biome-ignore lint/suspicious/noGlobalIsNan: <>
	if (input === '' || isNaN(parsedInput)) {
		return null
	}
	return parsedInput
}
function updatedParseFloatToZero(input: any) {
	const parsedInput = Number.parseFloat(input)
	// biome-ignore lint/suspicious/noGlobalIsNan: <>
	if (input === '' || isNaN(parsedInput)) {
		return 0
	}
	return parsedInput
}

function checkNumber(input: any) {
	if (input === 0) {
		return 0
	}
	return null
}

function getEmailDomain(input: string) {
	const indexOfAt = input.indexOf('@')
	const inputAfterAt = input.slice(indexOfAt + 1)
	const indexOfPeriod = inputAfterAt.indexOf('.')
	const result = inputAfterAt.slice(0, indexOfPeriod)
	return result
}

function getQuoteTypeColors(quoteType: QuoteType) {
	switch (quoteType) {
		case 'frenchApfp':
			return '#B4F8C8'
		case 'frenchPostProd':
			return '#FBE7C6'
		case 'aicp':
			return '#A0E7E5'
		case 'photoGrid':
			return '#FFAEBC'
		case 'soundProd':
			return '#B4F8C8'
		case 'cnc5ch':
			return '#B4F8C8'
		case 'quoteAnimationOnly':
			return '#B4F8C8'
		case 'quoteAnimationAndLiveAction':
			return '#B4F8C8'
		case 'blankQuote':
			return '#B4F8C8'
		case 'quoteDigital':
			return '#B4F8C8'
		case '360Template':
			return '#B4F8C8'
		default: {
			const tsCheck: never = quoteType
			return '#B4F8C8'
		}
	}
}
function getQuoteColor(filled: boolean) {
	switch (filled) {
		case true:
			return '#364263'
		default:
			return '#5e6782'
	}
}

function getCurrencySymbol(quote?: ReduxQuote | null) {
	if (quote?.quoteCurrency) {
		const currencyObject = currency.filter(
			(c) => c.code === quote.quoteCurrency
		)
		return currencyObject[0].symbol
	}
	return '€'
}

// Check if any new object keys are present in the object that is not there in the array of strings
function checkNewObjectKeysPresent(obj: any, arr: any[]) {
	const objKeys = Object.keys(obj)
	return objKeys.some((item) => !arr.includes(item))
}

// get the zero pad for project and quote numbers
function zeroPad(num: any, places: number) {
	return String(num).padStart(places, '0')
}

type CompanyProjectCountType = {
	name: string | null | undefined
	type: string | null | undefined
	count: number | null | undefined
	year: string | null | undefined
}

function getQuoteOrProjectNumberDetails({
	companyCount = [],
	type,
}: {
	companyCount: CompanyProjectCountType[]
	type: string
}) {
	let newTypeCount = '0'
	const newTypeYear = dayjs().toISOString()
	let typeDetails = companyCount.find((t) => t.type === type)
	// If type details doesn't exist we create it and push it to companyCount
	const newName = type.toUpperCase()
	if (!typeDetails) {
		typeDetails = {
			type,
			count: 0,
			year: dayjs().toISOString(),
			name: newName,
		} as CompanyProjectCountType
		companyCount.push(typeDetails)
	}
	if (typeDetails.name === null) {
		typeDetails.name = newName
	}
	if (typeDetails.count === 0) {
		newTypeCount = zeroPad(1, 4)
	} else {
		newTypeCount = zeroPad((typeDetails?.count || 0) + 1, 4)
	}
	if (!typeDetails.year) {
		typeDetails.year = newTypeYear
	} else {
		const today = dayjs().year()
		const oldTypeYear = dayjs(typeDetails.year).year()
		if (today > oldTypeYear) {
			typeDetails.count = 0
			typeDetails.year = newTypeYear
			newTypeCount = zeroPad(1, 4)
		}
	}
	typeDetails.count = (typeDetails.count || 0) + 1

	// companycount should have latest values because typeDetail is a refrence
	return { companyCount, newTypeCount, newTypeYear }
}

function createProjectTypeDetails(type: string) {
	let newName: string
	if (type === 'advertisingProduction') {
		newName = 'AdvertisingProduction'
	} else if (type === 'imageAndSound') {
		newName = 'Image&Sound'
	} else if (type === 'productionShort') {
		newName = 'ProductionShort'
	} else if (type === 'animation') {
		newName = 'Animation'
	} else if (type === 'photoProduction') {
		newName = 'PhotoProduction'
	} else if (type === 'featureFilm') {
		newName = 'FeatureFilm'
	} else if (type === 'series') {
		newName = 'Series'
	} else if (type === 'other') {
		newName = 'P'
	} else if (type === '360Template') {
		newName = '360'
	} else {
		newName = type.toUpperCase()
	}
	const newTypeDetails = {
		type,
		count: 0,
		year: dayjs().toISOString(),
		name: newName,
	} as CompanyProjectCountType
	return { newName, newTypeDetails }
}
function getProjectNumberDetails({
	companyCount = [],
	type,
}: {
	companyCount: CompanyProjectCountType[]
	type: string
}) {
	let newTypeCount = '0'
	const newTypeYear = dayjs().toISOString()
	let typeDetails = companyCount.find((t) => t.type === type)
	// If type details doesn't exist we create it and push it to companyCount
	if (!typeDetails) {
		const { newName, newTypeDetails } = createProjectTypeDetails(type)
		typeDetails = newTypeDetails
		companyCount.push(typeDetails)
	}
	if (typeDetails.count === 0) {
		newTypeCount = zeroPad(1, 4)
	} else {
		newTypeCount = zeroPad((typeDetails?.count || 0) + 1, 4)
	}
	if (!typeDetails.year) {
		typeDetails.year = newTypeYear
	} else {
		const today = dayjs().year()
		const oldTypeYear = dayjs(typeDetails.year).year()
		if (today > oldTypeYear) {
			typeDetails.count = 0
			typeDetails.year = newTypeYear
			newTypeCount = zeroPad(1, 4)
		}
	}
	typeDetails.count = (typeDetails.count || 0) + 1

	// companycount should have latest values because typeDetail is a refrence
	return { companyCount, newTypeCount, newTypeYear }
}

function capitalizeFirstLetter(text: string) {
	return text.charAt(0).toUpperCase() + text.slice(1)
}
function createQuoteTypeDetails(type: string) {
	const newName = capitalizeFirstLetter(type)
	const newTypeDetails = {
		type,
		count: 0,
		year: dayjs().toISOString(),
		name: newName,
	} as CompanyProjectCountType
	return { newName, newTypeDetails }
}
function getQuoteNumberDetails({
	companyCount = [],
	type,
}: {
	companyCount: CompanyProjectCountType[]
	type: string
}) {
	let newTypeCount = '0'
	const newTypeYear = dayjs().toISOString()
	let typeDetails = companyCount.find((t) => t.type === type)
	// If type details doesn't exist we create it and push it to companyCount
	if (!typeDetails) {
		const { newName, newTypeDetails } = createQuoteTypeDetails(type)
		typeDetails = newTypeDetails
		companyCount.push(typeDetails)
	}
	if (typeDetails.count === 0) {
		newTypeCount = zeroPad(1, 4)
	} else {
		newTypeCount = zeroPad((typeDetails?.count || 0) + 1, 4)
	}
	if (!typeDetails.year) {
		typeDetails.year = newTypeYear
	} else {
		const today = dayjs().year()
		const oldTypeYear = dayjs(typeDetails.year).year()
		if (today > oldTypeYear) {
			typeDetails.count = 0
			typeDetails.year = newTypeYear
			newTypeCount = zeroPad(1, 4)
		}
	}
	typeDetails.count = (typeDetails.count || 0) + 1

	// companycount should have latest values because typeDetail is a refrence
	return { companyCount, newTypeCount, newTypeYear }
}

function getQuoteNumber({
	newTypeCount,
	newTypeYear,
	type,
}: {
	newTypeCount: string
	newTypeYear: string
	type: string
}) {
	let quoteNumber = ''
	if (type === 'frenchApfp') {
		quoteNumber = `APFP-${dayjs(
			newTypeYear as string
		).year()}-${newTypeCount}`
	} else if (type === 'frenchPostProd') {
		quoteNumber = `POSTPROD-${dayjs(
			newTypeYear as string
		).year()}-${newTypeCount}`
	} else if (type === 'aicp') {
		quoteNumber = `AICP-${dayjs(
			newTypeYear as string
		).year()}-${newTypeCount}`
	} else if (type === 'photoGrid') {
		quoteNumber = `PHOTO-${dayjs(
			newTypeYear as string
		).year()}-${newTypeCount}`
	} else if (type === 'soundProd') {
		quoteNumber = `${dayjs(newTypeYear as string).year()}-d-${newTypeCount}`
	} else {
		// If qupte type is not updated it will use the type for the QuoteNumber
		quoteNumber = `${type.toUpperCase()}-${dayjs(
			newTypeYear as string
		).year()}-${newTypeCount}`
	}
	return quoteNumber
}

function getProjectNumber({
	newTypeCount,
	newTypeYear,
	type,
	useOneProjectNumber,
	commonProjectNumber,
	projectDetails = [],
	projectCustomisationDetails = [
		{
			seperator: '-',
			first: 'number',
			second: 'YYYY',
			third: 'name',
		},
	],
}: {
	newTypeCount: string
	newTypeYear: string
	type: string
	useOneProjectNumber: boolean | null
	commonProjectNumber: number | null
	projectDetails?: {
		name: string | null
		type: string | null
		count: number | null
		year: string | null
	}[]
	projectCustomisationDetails?: any
}) {
	console.log('projectCustomisationDetails: ', projectCustomisationDetails)
	// name,number,YYYY
	const typeDetails = projectDetails.find((t) => t.type === type)
	console.log('projectDetails: ', projectDetails)
	console.log('typeDetails: ', typeDetails)
	const name = typeDetails ? typeDetails.name : 'AdvertisingProduction'
	console.log('name: ', name)
	let number: any = newTypeCount
	if (useOneProjectNumber) {
		number = commonProjectNumber
	}
	let projectNumber = ''
	const YY = dayjs(newTypeYear as string)
		.year()
		.toString()
		.substring(2)

	const YYYY = dayjs(newTypeYear as string)
		.year()
		.toString()
	let data = {
		name: name,
		number: number,
		YYYY: YYYY,
		YY: YY,
	}
	if (
		!projectCustomisationDetails ||
		projectCustomisationDetails.length === 0
	) {
		if (projectTypes.includes(type)) {
			projectNumber = `${name}-${YY}-${newTypeCount}`
		} else {
			// If quote type is not updated it will use the type for the projectNumber
			projectNumber = `${type.toUpperCase()}-${YY}-${newTypeCount}`
		}
		return projectNumber
	}

	if (!projectTypes.includes(type)) {
		data = {
			name: type.toUpperCase(),
			number: number,
			YYYY: YYYY,
			YY: YY,
		}
	}
	console.log('type: ', type)
	console.log('data: ', data)
	// switch (type) {
	//   case 'frenchApfp':
	//   case 'frenchPostProd':
	//   case 'aicp':
	//   case 'photoGrid':
	//     break
	//   default:
	//     data = {
	//       name: type.toUpperCase(),
	//       number: number,
	//       YYYY: YYYY,
	//       YY: YY,
	//     }
	// }

	if (projectCustomisationDetails[0].seperator === '') {
		projectNumber = `${
			data[projectCustomisationDetails[0]?.first as keyof typeof data]
		}${data[projectCustomisationDetails[0]?.second as keyof typeof data]}${
			data[projectCustomisationDetails[0]?.third as keyof typeof data]
		}`
	} else {
		projectNumber = `${
			data[projectCustomisationDetails[0]?.first as keyof typeof data]
		}${projectCustomisationDetails[0]?.seperator}${
			data[projectCustomisationDetails[0]?.second as keyof typeof data]
		}${projectCustomisationDetails[0]?.seperator}${
			data[projectCustomisationDetails[0]?.third as keyof typeof data]
		}`
	}

	return projectNumber
}
function getQuoteNumberNew({
	newTypeCount,
	newTypeYear,
	type,
	useOneQuoteNumber,
	commonQuoteNumber,
	quoteDetails = [],
	quoteCustomisationDetails = [
		{
			seperator: '-',
			first: 'number',
			second: 'YYYY',
			third: 'name',
		},
	],
}: {
	newTypeCount: string
	newTypeYear: string
	type: string
	useOneQuoteNumber: boolean | null
	commonQuoteNumber: number | null
	quoteDetails?: {
		name: string | null
		type: string | null
		count: number | null
		year: string | null
	}[]
	quoteCustomisationDetails?: any
}) {
	// name,number,YYYY
	const typeDetails = quoteDetails.find((t) => t.type === type)

	let name = type.toUpperCase()
	if (typeDetails && (typeDetails.name || typeDetails.name === '')) {
		name = typeDetails.name
	}
	let number: any = newTypeCount
	if (useOneQuoteNumber) {
		number = zeroPad(commonQuoteNumber, 4)
	}
	let quoteNumber = ''
	const YY = dayjs(newTypeYear as string)
		.year()
		.toString()
		.substring(2)

	const YYYY = dayjs(newTypeYear as string)
		.year()
		.toString()
	let data = {
		name: name,
		number: number,
		YYYY: YYYY,
		YY: YY,
	}
	if (!quoteCustomisationDetails || quoteCustomisationDetails.length === 0) {
		if (quoteTypes.includes(type as any)) {
			quoteNumber = `${name}-${YY}-${newTypeCount}`
		} else {
			// If quote type is not updated it will use the type for the quoteNumber
			quoteNumber = `${type.toUpperCase()}-${YY}-${newTypeCount}`
		}
		return quoteNumber
	}

	if (!quoteTypes.includes(type as any)) {
		data = {
			name: type.toUpperCase(),
			number: number,
			YYYY: YYYY,
			YY: YY,
		}
	}
	console.log('type: ', type)
	console.log('data: ', data)

	if (quoteCustomisationDetails[0].seperator === '') {
		quoteNumber = `${
			data[quoteCustomisationDetails[0]?.first as keyof typeof data]
		}${data[quoteCustomisationDetails[0]?.second as keyof typeof data]}${
			data[quoteCustomisationDetails[0]?.third as keyof typeof data]
		}`
	} else {
		quoteNumber = `${
			data[quoteCustomisationDetails[0]?.first as keyof typeof data]
		}${quoteCustomisationDetails[0]?.seperator}${
			data[quoteCustomisationDetails[0]?.second as keyof typeof data]
		}${quoteCustomisationDetails[0]?.seperator}${
			data[quoteCustomisationDetails[0]?.third as keyof typeof data]
		}`
	}

	return quoteNumber
}

function getRowColor(rowColor: string, fontSize: number) {
	return css`
    --row-color: ${rowColor};
    --font-size: ${fontSize};
    .rdg-cell {
      background-color: var(--row-color);
      color: #336b87;
      font-size: var(--font-size);
    }
  `
}
function getCompanyInfoDetails({
	companyInfo,
}: {
	companyInfo?: ReduxCompany | null
}) {
	const emptyCompanyInfo = {
		name: '',
		phone: '',
		website: '',
		addressLine1: '',
		addressLine2: '',
		registrationNumber: '',
		vatNumber: '',
	} as ReduxCompany
	if (companyInfo && Object.keys(companyInfo).length > 0) {
		return companyInfo
	}
	return emptyCompanyInfo
}

async function updateCompanyInfoWithDeltaSyncSuppress(
	updatedCompanyInfoDetails: Partial<ReduxCompany>
) {
	let updatedCompany = {} as ReduxCompany
	try {
		const {
			data: { updateCompany },
		} = (await API.graphql(
			graphqlOperation(mutations.updateCompany, {
				input: updatedCompanyInfoDetails,
			})
		)) as { data: { updateCompany: ReduxCompany } }
		updatedCompany = updateCompany
	} catch (err: any) {
		const errMessage = err?.errors?.[0]?.message
		if (
			errMessage &&
			(errMessage === 'Failed to write delta sync record.' ||
				errMessage === 'DeltaSyncWriteError')
		) {
			console.error('DeltaSyncWriteError: ', err)
			updatedCompany = err?.errors?.[0]?.data
		} else {
			return captureExceptionAndThrow(err, {
				location:
					'miscellaneous.updateCompanyInfoWithDeltaSyncSuppress',
				companyId: updatedCompanyInfoDetails?.id,
				requestedFields: Object.keys(updatedCompanyInfoDetails ?? {}),
			})
		}
	}
	return updatedCompany
}

function removeExtraSpaces(str: string): string {
	return str.replace(/\s+/g, ' ').trim()
}

function normalizeNewlines(text: string): string {
	// Replace all types of newline characters with '\n'
	let replaced = text.replace(/(\r\n|\r|\n)/g, '\n')

	// Limit consecutive newline characters to a maximum of 2
	replaced = text.replace(/\n{3,}/g, '\n\n')

	replaced = replaced.replace(/\n/g, ' \n')

	return replaced
}

function splitStringIntoLines(
	str: string,
	initialMaxChunkLength = 10250
): string[][] {
	const chunks: string[] = []
	let currentChunk = ''
	let maxChunkLength = initialMaxChunkLength
	const normalizedStr = normalizeNewlines(str)
	// console.log('normalizedStr: ', JSON.stringify(normalizedStr))
	let currentWord = ''
	for (const char of normalizedStr) {
		if (char === ' ' || char === '\n' || char === '\t') {
			if (currentWord) {
				const potentialChunk = `${currentChunk}${currentWord}`
				if (potentialChunk.length > maxChunkLength) {
					chunks.push(currentChunk)
					currentChunk = currentWord
					maxChunkLength = initialMaxChunkLength
				} else {
					currentChunk = potentialChunk
				}
				currentWord = ''
			}

			const potentialChunk = `${currentChunk}${char}`
			if (char === '\n') {
				maxChunkLength -= 60
			}
			if (potentialChunk.length > maxChunkLength) {
				chunks.push(currentChunk)
				currentChunk = char
				maxChunkLength = initialMaxChunkLength
			} else {
				currentChunk = potentialChunk
			}
		} else {
			currentWord += char
		}
	}

	if (currentWord) {
		const potentialChunk = `${currentChunk}${currentWord}`
		if (potentialChunk.length > maxChunkLength) {
			chunks.push(currentChunk)
			currentChunk = currentWord
		} else {
			currentChunk = potentialChunk
		}
	}

	if (currentChunk) {
		chunks.push(currentChunk)
	}

	chunks.map((c, i) => console.log(i, ' chunk length: ', c.length))
	const pairedChunks = pairStrings(chunks)
	// console.log('pairedChunks: ', pairedChunks)
	return pairedChunks
}

function pairStrings(inputArray: string[]): string[][] {
	const pairedArray: string[][] = []

	for (let i = 0; i < inputArray.length; i += 2) {
		if (i + 1 <= inputArray.length) {
			const pair = [inputArray[i], inputArray[i + 1] ?? '']
			pairedArray.push(pair)
		}
	}

	return pairedArray
}

function combineStringArrayIntoColumns(inputArray: string[], maxLines = 112) {
	// You combine the max Lines number of indexes in inputArray into a single string
	const combinedArray: string[] = []
	let combinedString = ''
	for (let i = 0; i < inputArray.length; i++) {
		if (i % maxLines === 0 && i > 0) {
			combinedArray.push(combinedString)
			combinedString = ''
		}

		const currentString = inputArray[i]
		combinedString = `${combinedString}${currentString}`
		if ([0, 1, 2, 3].some((i2) => i2 === i)) {
			console.log('i: ', i)
			console.log('inputArray[i]: ', JSON.stringify(inputArray[i]))
			console.log('combinedString: ', JSON.stringify(combinedString))
		}
	}
	return combinedArray
}

export function getCurrencyCodeFromTimezone() {
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
	const countryObject = ct.getCountryForTimezone(timezone)
	const countryCode = countryObject?.id
	if (
		!countryCode ||
		typeof countryCode !== 'string' ||
		countryCode.length === 0
	) {
		return 'EUR'
	}
	const currencyCode =
		countryToCurrency?.[countryCode as keyof typeof countryToCurrency]
	return currencyCode || 'EUR'
}
function onlyUnique(value: string, index: number, array: string[]) {
	return array.indexOf(value) === index
}
function createDictionaryFromId<T extends { id: string }>(dataArray: T[]) {
	const dictionary: Record<string, T> = {}
	for (const obj of dataArray) {
		const key = obj.id
		dictionary[key] = obj
	}
	return dictionary
}

export const payrollAndInsuranceUuids = [
	'9bea0ad1-4725-4d37-af82-13f5e7102b7a', //apfp payroll
	'6b081d47-6f62-481f-9306-d4a29ac44fea', //apfp payroll int
	'17ca1d78-ebf1-4ce4-a546-d0f8ad84f81d', //apfp insurance
	'46a81fd5-b443-419b-bb01-827e8d322c17', //apfp insurance int

	'b595df21-2065-4417-9ca8-ee4814f2fede', //photo payroll
	'f5847d66-8e84-4245-8827-926701c23005', // photo insurance

	'b41a0ecb-64bd-47df-8f53-301b6479888d', // sound payroll
	'a260b28e-ad66-48c5-885b-a956a0053edb', //sound insurance

	'f7331894-0765-4d51-87a1-aee0469d892b', //aicp insurance and tax

	'cfb3fa11-0afb-4609-923e-1734e28d1614', // animation and live action payroll
	'da2d05cf-d21b-4b40-8cb2-3a8b2b06bbeb', // animation and live action insurance

	'1520e88b-3221-4e0f-b575-3a0956cef872', // animation only payroll
	'1e0e98f2-f024-414d-b7a3-267d83c06666', // animation only insurance

	'9c4dbdb4-469c-4fd8-9187-e105ecbf217d', // cnc5ch payroll and insurance

	'a4431dae-ec7e-4d79-8cc0-dee304ddfca4', // quoteDigital payroll
	'4550e341-16d2-469e-8a28-2cf552d0d890', // quoteDigital insurance

	'f0533c3b-e36c-48f6-abb5-2218f505f7c0', // 360 payroll
	'873b0b37-71e5-47ab-9482-495873ba71ae', // 360 payroll int
	'8fd28f90-7759-45cf-8ac1-36b6cc1c57eb', // 360 insurance
	'66bd2627-d5e7-4329-97d9-dc7bcd71373c', // 360 insurance int
]

function cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
	if (obj === null || obj === undefined) {
		return {}
	}

	if (Array.isArray(obj)) {
		return obj
			.map((item) => cleanObject(item))
			.filter((item) => item !== null && item !== undefined) as any
	}

	if (typeof obj !== 'object') {
		return obj
	}

	const cleaned: Partial<T> = {}

	for (const [key, value] of Object.entries(obj)) {
		if (value === null || value === undefined) {
			continue
		}

		if (typeof value === 'object') {
			const cleanedValue = cleanObject(value)
			if (Object.keys(cleanedValue).length > 0) {
				cleaned[key as keyof T] = cleanedValue as any
			}
		} else {
			cleaned[key as keyof T] = value
		}
	}

	return cleaned
}

// Simple function to calculate rate between currencies
function calculateExchangeRate(
	baseCurrency: string,
	targetCurrency: string,
	currencyData: any
): number {
	try {
		const { base, rates } = currencyData

		// If same currency, rate is 1
		if (baseCurrency === targetCurrency) return 1

		// Helper to get rate from base to any currency
		function rateBaseTo(cur: string): number {
			if (cur === base) return 1
			const entry = rates?.find((r: any) => r?.currencyName === cur)
			if (!entry?.currencyRate) {
				captureExceptionAndThrow(
					new Error(`No rate for currency "${cur}"`),
					{
						location: 'miscellaneous.calculateExchangeRate',
						missingCurrency: cur,
						baseCurrency,
						targetCurrency,
						availableRateCount: rates?.length,
					}
				)
			}
			return entry.currencyRate
		}

		const rBaseToFrom = rateBaseTo(baseCurrency)
		const rBaseToTo = rateBaseTo(targetCurrency)

		// Formula: rate(A→B) =r_{b→A}  / r_{b→B}
		return rBaseToFrom / rBaseToTo
	} catch (error) {
		console.log('Error calculating exchange rate:', error)
		return 1 // Default to 1 if calculation fails
	}
}

export {
	calculateExchangeRate,
	checkNewObjectKeysPresent,
	checkNumber,
	cleanObject,
	createDictionaryFromId,
	createProjectTypeDetails,
	createQuoteTypeDetails,
	delayInMs,
	getCompanyInfoDetails,
	getCurrencySymbol,
	getEmailDomain,
	getProjectNumber,
	getProjectNumberDetails,
	getQuoteColor,
	getQuoteNumber,
	getQuoteNumberDetails,
	getQuoteNumberNew,
	getQuoteOrProjectNumberDetails,
	getQuoteTypeColors,
	getRowColor,
	onlyUnique,
	removeExtraSpaces,
	splitStringIntoLines,
	updateCompanyInfoWithDeltaSyncSuppress,
	updatedParseFloat,
	updatedParseFloatToZero,
	zeroPad,
}
